/* ============================================================================
   Customers — for the person who bought the order.
   ----------------------------------------------------------------------------
   Serves both /customers and /for-customers from one implementation.

   This page has a different job from every other public route: someone
   reaching it usually has a problem right now. So it is task-first — the two
   things they came to do are in the hero, and the explanation follows. The
   old version opened with the same marketing-hero shape as the merchant page
   and put the actual claim routes further down.

   Tone matters here too: this reader is not evaluating a platform, they are
   trying to get help.
   ========================================================================== */
import { PageMeta } from '../components/PageMeta';
import { ScrollReveal } from '../components/ScrollReveal';
import { Band, Btn, Card, Grid, SectionHead, Split, ActionLink, Plate } from '../site/primitives';
import { AstronautArt, Checklist, FinalCTA, Hero } from '../site/sections';
import { ProtectionPath } from '../site/ProtectionPath';
import { Icon, type IconName } from '../site/icons';
import { ART } from '../site/media';

const steps = [
  { label: 'Find your merchant’s link', detail: 'Claim forms are specific to the store you bought from. The link is in your order communication.', icon: 'search' as const },
  { label: 'Verify your order', detail: 'Confirm the order and choose which items are affected — taken from the real order, not typed in.', icon: 'shield-check' as const },
  { label: 'Add what helps', detail: 'Supported photos and PDF documents can accompany the claim when they are relevant.', icon: 'file' as const },
  { label: 'Track it privately', detail: 'You receive a private token. Use it to follow the claim — no account required.', icon: 'lock' as const },
];

const issues: Array<{ icon: IconName; title: string; body: string }> = [
  { icon: 'file', title: 'Item not received', body: 'Guidance for when an expected item has not arrived.' },
  { icon: 'alert', title: 'Damaged item', body: 'What to include when describing the condition of an item.' },
  { icon: 'refresh', title: 'Wrong item received', body: 'How to report an item that does not match the order.' },
  { icon: 'clipboard', title: 'Missing parts', body: 'What to provide when an order arrives incomplete.' },
  { icon: 'wallet', title: 'Returns and refunds', body: 'Finding the right merchant or claim support path.' },
  { icon: 'lock', title: 'Keeping access private', body: 'Why your tracking token should stay confidential.' },
];

export function CustomersPage() {
  return (
    <>
      <PageMeta
        title="Enshield for customers"
        description="File and track an Enshield Protection claim with your order number, or your merchant’s claim link."
      />

      <Hero
        titleId="customers-title"
        eyebrow="We’re here to help"
        title={<>Order gone wrong? Let’s fix it.</>}
        lead="If you added Enshield Protection to your order, you can file a claim and follow it privately — without creating an account."
        actions={
          <>
            <Btn to="/file-a-claim">File a claim</Btn>
            <Btn tone="secondary" to="/track-claim">Track a claim</Btn>
          </>
        }
        art={<AstronautArt {...ART.protect} drift priority />}
      />

      {/* What to expect. */}
      <Band space="standard" tone="alt" labelledBy="customers-steps-title">
        <div className="u-stack u-stack--xl">
          <ScrollReveal>
            <SectionHead
              align="center"
              eyebrow="What to expect"
              titleId="customers-steps-title"
              title="Four steps, start to finish"
              lead="Claims start from the store you bought from, so your order details are already known."
            />
          </ScrollReveal>
          <ProtectionPath stages={steps} label="Filing a claim" />
        </div>
      </Band>

      {/* Why it works this way — transparency. */}
      <Band space="spacious" labelledBy="customers-trust-title">
        <Split weight="copy">
          <ScrollReveal className="u-stack u-stack--lg">
            <SectionHead
              eyebrow="How your claim is handled"
              titleId="customers-trust-title"
              title="Clear, private, and tied to your real order"
              lead="A few deliberate choices about how claims work, and why they are made that way."
            />
            <Checklist
              items={[
                'Protection is something you chose at checkout — it is never added for you',
                'Your merchant’s link identifies the right store, so Enshield can handle the claim correctly',
                'Your order is verified before the claim is submitted',
                'Your tracking token is private: keep it to yourself and never share it publicly',
              ]}
            />
            <div className="u-cluster">
              <Btn tone="secondary" to="/how-it-works">See the full flow</Btn>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.08}>
            <AstronautArt {...ART.magnifier} support />
          </ScrollReveal>
        </Split>
      </Band>

      {/* Common situations. */}
      <Band space="standard" tone="alt" labelledBy="customers-issues-title">
        <ScrollReveal className="u-stack u-stack--xl">
          <SectionHead
            align="center"
            eyebrow="Common situations"
            titleId="customers-issues-title"
            title="Find the guidance that matches your order"
          />
          <Grid cols={3}>
            {issues.map((issue) => (
              <Card key={issue.title} to="/faq">
                <Plate><Icon name={issue.icon} /></Plate>
                <h3 className="u-h4">{issue.title}</h3>
                <p className="u-small">{issue.body}</p>
              </Card>
            ))}
          </Grid>
          <div className="u-cluster u-cluster--center">
            <ActionLink to="/faq">Read all help articles</ActionLink>
          </div>
        </ScrollReveal>
      </Band>

      {/*
        * This callout used to route the shopper back to the merchant and then
        * to support, because a claim link was once the only way in. It is not:
        * the order number and the email on the order identify the store, and
        * the form is right here. Sending someone away from a working route is
        * a dead end we created.
        */}
      <Band space="compact" labelledBy="customers-help-title">
        <ScrollReveal className="p-callout">
          <div className="u-stack u-stack--sm">
            <h2 id="customers-help-title" className="u-h3">Can’t find a claim link?</h2>
            <p className="u-body">
              You don’t need one. Start at File a claim with your order number and the email address on the
              order — we’ll find the store. If that doesn’t work, contact us and a person will help.
            </p>
          </div>
          <div className="u-cluster">
            <Btn tone="primary" to="/file-a-claim">File a claim</Btn>
            <Btn tone="secondary" to="/contact">Contact Enshield</Btn>
          </div>
        </ScrollReveal>
      </Band>

      <FinalCTA
        titleId="customers-cta-title"
        title="Ready when you are"
        body="Start a claim with your order number, or check on one you’ve already filed."
        primary={{ label: 'File a claim', to: '/file-a-claim' }}
        secondary={{ label: 'Track a claim', to: '/track-claim' }}
      />
    </>
  );
}
